/**
 * DecoEuropean theme behaviours.
 * Small, dependency-free helpers for the custom sections. Horizon's own
 * components (cart drawer, predictive search, variant picker, product form)
 * keep handling commerce; this file only covers presentation.
 */
(function () {
  'use strict';

  const BREAKPOINT = 1024;

  /* ---------- Header: measured height, mega menu, mobile menu ---------- */
  function initHeader() {
    const header = document.querySelector('[data-de-header]');
    if (!header) return;

    const setHeight = () => {
      document.documentElement.style.setProperty('--de-header-height', header.offsetHeight + 'px');
      document.body.style.setProperty('--header-height', header.offsetHeight + 'px');
      document.body.style.setProperty('--header-group-height', header.offsetHeight + 'px');
    };
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);
    else window.addEventListener('resize', setHeight);

    // Mega menu (desktop)
    const items = header.querySelectorAll('[data-de-dept]');
    const panels = header.querySelectorAll('[data-de-mega]');
    let closeTimer = null;

    const closeAll = () => {
      panels.forEach((p) => { p.hidden = true; });
      items.forEach((i) => { i.setAttribute('aria-expanded', 'false'); i.classList.remove('is-open'); });
    };
    const open = (id) => {
      closeAll();
      const panel = header.querySelector('[data-de-mega="' + id + '"]');
      const item = header.querySelector('[data-de-dept="' + id + '"]');
      if (!panel || !item) return;
      panel.hidden = false;
      item.setAttribute('aria-expanded', 'true');
      item.classList.add('is-open');
    };

    items.forEach((item) => {
      const id = item.getAttribute('data-de-dept');
      const hasPanel = !!header.querySelector('[data-de-mega="' + id + '"]');
      if (!hasPanel) {
        item.addEventListener('mouseenter', closeAll);
        return;
      }
      item.addEventListener('mouseenter', () => { clearTimeout(closeTimer); open(id); });
      item.addEventListener('focus', () => open(id));
      item.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); open(id); const first = header.querySelector('[data-de-mega="' + id + '"] a'); if (first) first.focus(); }
        if (e.key === 'Escape') closeAll();
      });
    });
    header.querySelectorAll('[data-de-mega-close]').forEach((el) => el.addEventListener('mouseenter', closeAll));
    header.addEventListener('mouseleave', () => { closeTimer = setTimeout(closeAll, 120); });
    panels.forEach((p) => {
      p.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      p.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeAll(); const id = p.getAttribute('data-de-mega'); const item = header.querySelector('[data-de-dept="' + id + '"]'); if (item) item.focus(); } });
    });
    document.addEventListener('focusin', (e) => { if (!header.contains(e.target)) closeAll(); });

    // Mobile menu
    const toggle = header.querySelector('[data-de-menu-toggle]');
    const menu = header.querySelector('[data-de-mobile-menu]');
    if (toggle && menu) {
      const iconOpen = toggle.querySelector('[data-de-icon="menu"]');
      const iconClose = toggle.querySelector('[data-de-icon="x"]');
      const setOpen = (isOpen) => {
        menu.hidden = !isOpen;
        toggle.setAttribute('aria-expanded', String(isOpen));
        if (iconOpen) iconOpen.hidden = isOpen;
        if (iconClose) iconClose.hidden = !isOpen;
        document.documentElement.classList.toggle('de-menu-open', isOpen);
      };
      toggle.addEventListener('click', () => setOpen(menu.hidden));
      window.addEventListener('resize', () => { if (window.innerWidth >= BREAKPOINT && !menu.hidden) setOpen(false); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !menu.hidden) { setOpen(false); toggle.focus(); } });
    }
  }

  /* ---------- Tabs ---------- */
  function initTabs() {
    document.querySelectorAll('[data-de-tabs]').forEach((root) => {
      const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
      const panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));
      if (!tabs.length) return;
      const select = (tab, focus) => {
        tabs.forEach((t) => { const on = t === tab; t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; });
        panels.forEach((p) => { p.hidden = p.id !== tab.getAttribute('aria-controls'); });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          let n = null;
          if (e.key === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
          if (e.key === 'Home') n = tabs[0];
          if (e.key === 'End') n = tabs[tabs.length - 1];
          if (n) { e.preventDefault(); select(n, true); }
        });
      });
      const hash = location.hash && tabs.find((t) => '#' + t.id === location.hash);
      select(hash || tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0]);
    });
  }

  /* ---------- Simple gallery (fallback when Horizon's media gallery is not used) ---------- */
  function initGalleries() {
    document.querySelectorAll('[data-de-gallery]').forEach((g) => {
      const main = g.querySelector('[data-de-gallery-main]');
      const thumbs = Array.from(g.querySelectorAll('[data-de-gallery-thumb]'));
      const count = g.querySelector('[data-de-gallery-count]');
      if (!main || !thumbs.length) return;
      let index = 0;
      const show = (i) => {
        index = (i + thumbs.length) % thumbs.length;
        const t = thumbs[index];
        const img = t.querySelector('img');
        if (img) { main.src = img.dataset.full || img.src; main.srcset = img.dataset.fullSrcset || ''; main.alt = img.alt || ''; }
        thumbs.forEach((x, j) => x.setAttribute('aria-current', String(j === index)));
        if (count) count.textContent = String(index + 1).padStart(2, '0') + ' / ' + String(thumbs.length).padStart(2, '0');
      };
      thumbs.forEach((t, i) => t.addEventListener('click', () => show(i)));
      g.querySelector('[data-de-gallery-prev]')?.addEventListener('click', () => show(index - 1));
      g.querySelector('[data-de-gallery-next]')?.addEventListener('click', () => show(index + 1));
      show(0);
    });
  }

  /* ---------- Sticky add-to-basket bar on product pages ---------- */
  function initStickyBar() {
    const bar = document.querySelector('[data-de-stickybar]');
    const anchor = document.querySelector('[data-de-stickybar-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { bar.dataset.visible = String(!en.isIntersecting && en.boundingClientRect.top < 0); });
    }, { threshold: 0 });
    io.observe(anchor);
    const trigger = bar.querySelector('[data-de-stickybar-submit]');
    if (trigger) {
      trigger.addEventListener('click', () => {
        const form = document.querySelector('form[data-type="add-to-cart-form"]');
        const btn = form && form.querySelector('button[type="submit"][name="add"], .add-to-cart-button');
        if (btn) btn.click();
        else if (form) form.requestSubmit();
      });
    }
  }

  /* ---------- Facets: submit on change, mobile toggle ---------- */
  function initFacets() {
    document.querySelectorAll('form[data-de-facets]').forEach((form) => {
      let timer = null;
      form.addEventListener('change', (e) => {
        if (e.target.matches('input[type="number"]')) return;
        clearTimeout(timer);
        timer = setTimeout(() => form.requestSubmit(), 250);
      });
    });
    document.querySelectorAll('[data-de-facets-toggle]').forEach((btn) => {
      const target = document.getElementById(btn.getAttribute('aria-controls'));
      if (!target) return;
      btn.addEventListener('click', () => {
        const open = target.hidden;
        target.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
      });
    });
    document.querySelectorAll('select[data-de-sort]').forEach((sel) => {
      sel.addEventListener('change', () => {
        const url = new URL(location.href);
        url.searchParams.set('sort_by', sel.value);
        url.searchParams.delete('page');
        location.href = url.toString();
      });
    });
  }

  /* ---------- Variant sync: keep custom buy-box fragments in step with Horizon's variant picker ---------- */
  function initVariantSync() {
    document.querySelectorAll('[data-de-variant-sync]').forEach((el) => {
      const section = el.closest('.shopify-section');
      if (!section) return;
      section.addEventListener('shopify:product:select', (event) => {
        const promise = event.promise;
        if (!promise) return;
        promise.then((result) => {
          const html = result && result.detail && result.detail.html;
          if (!html) return;
          const key = el.getAttribute('data-de-variant-sync');
          const next = html.querySelector('[data-de-variant-sync="' + key + '"]');
          if (next) el.innerHTML = next.innerHTML;
        }).catch(() => {});
      });
    });
  }


  /* ---------- Header basket total ---------- */
  function initCartTotal() {
    const targets = document.querySelectorAll('[data-de-cart-total]');
    if (!targets.length) return;
    const format = (cents, fmt) => {
      const n = (cents / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const whole = Math.round(cents / 100).toLocaleString('en-GB');
      return fmt.replace(/\{\{\s*amount_no_decimals\s*\}\}/, whole).replace(/\{\{\s*amount[a-z_]*\s*\}\}/, n);
    };
    const refresh = () => {
      fetch((window.Theme && Theme.routes && Theme.routes.cart_url ? Theme.routes.cart_url : '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          targets.forEach((t) => { t.textContent = format(cart.total_price, t.dataset.moneyFormat || '£{{amount}}'); });
        })
        .catch(() => {});
    };
    document.addEventListener('shopify:cart:lines-update', (e) => { if (e.promise) e.promise.then(refresh).catch(() => {}); else refresh(); });
    document.addEventListener('shopify:cart:discount-update', refresh);
    window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });
  }

  /* ---------- Scroll container hand-over at Horizon's 990px squeeze breakpoint ---------- */
  // Above 990px Horizon scrolls .page-wrapper and keeps html/body at overflow:hidden; below it the document
  // scrolls. When the viewport crosses that line (theme editor desktop/mobile toggle, window resize, rotation)
  // the browser can leave the old container scrolled past its content, which shows an empty page. Carry the
  // position across and zero the container that no longer scrolls.
  function initScrollHandover() {
    if (window.__deScrollHandover || !('matchMedia' in window)) return;
    window.__deScrollHandover = true;
    const query = window.matchMedia('(min-width: 990px)');
    const doc = () => document.scrollingElement || document.documentElement;
    const wrapper = () => document.querySelector('.page-wrapper');
    let lastTop = 0;
    const track = () => { const w = wrapper(); lastTop = query.matches && w ? w.scrollTop : doc().scrollTop; };
    document.addEventListener('scroll', track, { capture: true, passive: true });
    const handover = () => {
      const w = wrapper();
      const top = lastTop;
      requestAnimationFrame(() => {
        if (query.matches && w) {
          doc().scrollTop = 0;
          w.scrollTo({ top: Math.min(top, w.scrollHeight - w.clientHeight), behavior: 'instant' });
        } else {
          if (w) w.scrollTop = 0;
          window.scrollTo({ top: Math.min(top, doc().scrollHeight - doc().clientHeight), behavior: 'instant' });
        }
        track();
      });
    };
    if (query.addEventListener) query.addEventListener('change', handover);
    else query.addListener(handover);
  }

  function init() {
    initScrollHandover();
    initHeader();
    initTabs();
    initGalleries();
    initStickyBar();
    initFacets();
    initVariantSync();
    if (!window.__deCartTotalInit) { window.__deCartTotalInit = true; initCartTotal(); }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Re-run when the theme editor re-renders a section
  document.addEventListener('shopify:section:load', init);
})();
